// portal-web-ui/src/components/analytics/WizardStepTracker.tsx
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { ReportContext, useReportAnalytics } from "@hooks/useReportAnalytics";

interface Props {
  reportContext: ReportContext;
  stepOrder: string[];
}

export function WizardStepTracker({ reportContext, stepOrder }: Props) {
  const { captureWizardStepAdvanced, captureWizardStepBack } = useReportAnalytics(reportContext);
  const { pathname } = useLocation();
  const previous = useRef<{ step: string; enteredAt: number; reportId: string } | null>(null);

  useEffect(() => {
    const currentStep = pathname.split("/").pop() ?? "unknown";
    const now = Date.now();
    const prev = previous.current;
    const next = { step: currentStep, enteredAt: now, reportId: reportContext.report_id };

    if (prev?.reportId !== reportContext.report_id) {
      previous.current = next;
      return;
    }
    if (prev.step === currentStep) return;
    // Skip the synthetic transition fired by the bare-URL → first-step redirect,
    // where prev.step is the reportId itself rather than a real step name.
    if (prev.step !== reportContext.report_id) {
      const seconds = Math.round((now - prev.enteredAt) / 1000);
      const fromIdx = stepOrder.indexOf(prev.step);
      const toIdx = stepOrder.indexOf(currentStep);
      const isForward = fromIdx !== -1 && toIdx !== -1 && toIdx > fromIdx;
      const fire = isForward ? captureWizardStepAdvanced : captureWizardStepBack;
      fire(prev.step, currentStep, seconds);
    }
    previous.current = next;
  }, [pathname, stepOrder, captureWizardStepAdvanced, captureWizardStepBack, reportContext.report_id]);

  return null;
}
